import _get from 'lodash/get';
import { GeoJsonObject } from 'geojson';
import { FilterGroupingEnum, TaskFilterInputObject } from 'graphql-common';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import { MapParams } from '@lib/components/Map/types';
import useSectorLocationCenterQueryHook from 'utils/fetch/hooks/useSectorLocationCenterQueryHook';
import useSiteLocationCenterQueryHook from 'utils/fetch/hooks/useSiteLocationCenterQueryHook';
import useSectorsPerformanceMetrics from 'utils/fetch/hooks/useSectorsPerformanceMetrics';
import useSitesPerformanceMetrics from 'utils/fetch/hooks/useSitesPerformanceMetrics';
import useSectorLocationsQueryHook from 'utils/fetch/hooks/useSectorLocationsQueryHook';
import useSiteLocationsQueryHook from 'utils/fetch/hooks/useSiteLocationsQueryHook';
import { MonitoringEntity } from '../enums';

export default function useMonitoringMapQueries({
  monitoringEntity,
  mapParams,
  showSitesOnSectors,
  taskFilters,
  statusKind,
}: {
  monitoringEntity: MonitoringEntity;
  mapParams: MapParams;
  showSitesOnSectors: boolean;
  taskFilters: TaskFilterInputObject[];
  statusKind?: any;
}) {
  const shouldFetchLocationCenter = !mapParams?.center;

  // Center queries
  const siteLocationCenterQueryHookResult = useSiteLocationCenterQueryHook({
    defaultFilters: [
      {
        kind: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: 'point' },
        },
      },
    ],
    skip:
      !shouldFetchLocationCenter ||
      !(
        monitoringEntity === MonitoringEntity.Site ||
        (monitoringEntity === MonitoringEntity.Sector && showSitesOnSectors)
      ),
  });

  const sectorLocationCenterQueryHookResult = useSectorLocationCenterQueryHook({
    defaultFilters: [
      {
        kind: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: 'boundary' },
        },
      },
    ],
    skip:
      !shouldFetchLocationCenter ||
      monitoringEntity !== MonitoringEntity.Sector,
  });

  const siteLocationCenterLoading =
    siteLocationCenterQueryHookResult.firstLoading;
  const sectorLocationCenterLoading =
    sectorLocationCenterQueryHookResult.firstLoading;

  // Locations
  const shouldFetchSiteLocations =
    !shouldFetchLocationCenter &&
    !siteLocationCenterQueryHookResult.firstLoading &&
    (monitoringEntity === MonitoringEntity.Site ||
      (monitoringEntity === MonitoringEntity.Sector && showSitesOnSectors));
  const siteLocationsQueryHookResult = useSiteLocationsQueryHook({
    mapParams,
    skip: !shouldFetchSiteLocations,
  });

  const shouldFetchSectorLocations =
    !shouldFetchLocationCenter &&
    !sectorLocationCenterQueryHookResult.firstLoading &&
    monitoringEntity === MonitoringEntity.Sector;
  const sectorLocationsQueryHookResult = useSectorLocationsQueryHook({
    mapParams,
    skip: !shouldFetchSectorLocations,
  });

  // Prepare separate map data for sites and sectors, then compose as needed
  const sitesMapData = removeTypenameDeep(
    siteLocationsQueryHookResult.data,
  ) as GeoJsonObject;
  const sectorsMapData = removeTypenameDeep(
    sectorLocationsQueryHookResult.data,
  ) as GeoJsonObject;

  let mapData: GeoJsonObject;
  if (monitoringEntity === MonitoringEntity.Site) {
    mapData = sitesMapData;
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    if (showSitesOnSectors) {
      const sectorFeatures = (sectorsMapData as any)?.features || [];
      const siteFeatures = (sitesMapData as any)?.features || [];
      mapData = {
        ...(sectorsMapData || {}),
        // Merge sector polygons with site points
        features: [...sectorFeatures, ...siteFeatures],
      } as unknown as GeoJsonObject;
    } else {
      mapData = sectorsMapData;
    }
  }

  const siteIds = _get(sitesMapData, 'features', []).map((feature) => {
    return _get(feature, 'properties.sourceables.0.id');
  });
  const sectorIds = _get(sectorsMapData, 'features', []).map((feature) => {
    return _get(feature, 'properties.sourceables.0.id');
  });

  const sitesPerformanceMetricsHookResult = useSitesPerformanceMetrics({
    additionalFilters: taskFilters,
    ids: siteIds,
    statusKind,
    skip:
      (monitoringEntity === MonitoringEntity.Sector && !showSitesOnSectors) ||
      !siteIds?.length,
  });
  const sectorsPerformanceMetricsHookResult = useSectorsPerformanceMetrics({
    additionalFilters: taskFilters,
    ids: sectorIds,
    statusKind,
    skip: monitoringEntity === MonitoringEntity.Site || !sectorIds?.length,
  });

  return {
    mapData,
    sectorLocationCenterQueryHookResult,
    sectorsPerformanceMetricsHookResult,
    siteLocationCenterQueryHookResult,
    siteLocationsQueryHookResult,
    sitesPerformanceMetricsHookResult,
    sectorLocationsQueryHookResult,
    siteLocationCenterLoading,
    sectorLocationCenterLoading,
  };
}
